import { useNavigate } from "react-router-dom";
import { MoreHorizontal } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * QuickActionsRow — exactly five (or fewer, role-dependent) buttons.
 *
 * Desktop:    all five visible in a horizontal row.
 * Mobile:     first three visible; the rest tuck behind a "More" dropdown.
 */
export function QuickActionsRow({ actions }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const primary = actions.slice(0, 3);
  const rest = actions.slice(3);

  const handle = (a) => {
    if (a.onClick) a.onClick();
    else if (a.url) nav(a.url);
  };

  return (
    <div
      className="flex flex-wrap items-stretch gap-2"
      data-testid="dashboard-quick-actions"
    >
      {/* Always visible */}
      {primary.map((a) => (
        <ActionButton key={a.label} a={a} onClick={() => handle(a)} testId={`qa-${a.key}`} />
      ))}
      {/* Desktop-only */}
      {rest.map((a) => (
        <div key={a.label} className="hidden md:flex">
          <ActionButton a={a} onClick={() => handle(a)} testId={`qa-${a.key}`} />
        </div>
      ))}
      {/* Mobile overflow */}
      {rest.length > 0 && (
        <div className="md:hidden">
          <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="qa-more"
                className="h-11 px-4 rounded-lg border border-[#E4E7EC] bg-white text-[#1d3557] text-[13px] font-semibold flex items-center gap-1 hover:border-[#457b9d]"
              >
                <MoreHorizontal size={16} />
                More
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
              {rest.map((a) => (
                <DropdownMenuItem key={a.label} onClick={() => handle(a)}>
                  <a.Icon size={14} className="mr-2" /> {a.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}

function ActionButton({ a, onClick, testId }) {
  const tone = a.tone || "default";
  const toneCls = {
    primary: "bg-[#e63946] hover:bg-[#d02f3c] text-white border-transparent",
    default: "bg-white text-[#1d3557] border-[#E4E7EC] hover:border-[#457b9d] hover:bg-[#F1FAEE]",
    frosted: "bg-[#a8dadc]/30 text-[#1d3557] border-transparent hover:bg-[#a8dadc]/50",
  }[tone];

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`h-11 px-4 rounded-lg border text-[13px] font-semibold flex items-center gap-2 transition-colors ${toneCls}`}
    >
      <a.Icon size={16} strokeWidth={2} />
      <span>{a.label}</span>
    </button>
  );
}
